// /contact — the support form. Public and bare (see chrome.ts's BARE_PATHS
// and main.ts's PUBLIC_PATHS), same as /privacy and /terms: replaces the
// plaintext mailto link that used to sit on both, which was scraper bait
// (flagged 2026-08-24, fixed 2026-08-30).

import { ApiClientError, sendContact } from '../api/client.js';
import { type View } from '../router.js';
import { el } from '../dom.js';
import { getRecaptchaToken, preloadRecaptcha } from '../recaptcha.js';
import { authBrand } from './authBrand.js';

export const contactView: View = (outlet) => {
  preloadRecaptcha();

  const error = el('p', { class: 'form-error', hidden: true });
  const submit = el('button', { type: 'submit', class: 'btn' }, 'Send message');

  const name = el('input', {
    id: 'name',
    name: 'name',
    type: 'text',
    required: true,
    autocomplete: 'name',
  }) as HTMLInputElement;

  const email = el('input', {
    id: 'email',
    name: 'email',
    type: 'email',
    required: true,
    autocomplete: 'email',
    inputmode: 'email',
    placeholder: 'you@example.com',
  }) as HTMLInputElement;

  const message = el('textarea', {
    id: 'message',
    name: 'message',
    required: true,
    rows: 6,
  }) as HTMLTextAreaElement;

  // Honeypot, same as /register: kept off-screen and out of the tab order.
  // Bots fill it; humans never see it. A filled value makes the server
  // respond like success and send nothing.
  const honeypot = el('input', {
    name: 'website',
    type: 'text',
    tabindex: -1,
    autocomplete: 'off',
    style: 'position:absolute;left:-9999px;width:1px;height:1px;',
    'aria-hidden': 'true',
  }) as HTMLInputElement;

  const form = el('form', { method: 'post', action: '/contact', novalidate: true }, [
    el('div', { class: 'field' }, [el('label', { for: 'name' }, 'Name'), name]),
    el('div', { class: 'field' }, [el('label', { for: 'email' }, 'Email'), email]),
    el('div', { class: 'field' }, [el('label', { for: 'message' }, 'Message'), message]),
    honeypot,
    error,
    submit,
  ]) as HTMLFormElement;

  form.addEventListener('submit', async (ev) => {
    ev.preventDefault();
    error.hidden = true;
    submit.setAttribute('disabled', 'disabled');
    try {
      const recaptchaToken = await getRecaptchaToken('contact');
      const { message: confirmation } = await sendContact({
        name: name.value.trim(),
        email: email.value.trim(),
        message: message.value.trim(),
        recaptchaToken,
        website: honeypot.value || undefined,
      });
      outlet.replaceChildren(
        el('main', { class: 'auth-card' }, [
          authBrand('h1'),
          el('p', {}, confirmation),
          el('p', { class: 'signed-out-only' }, [el('a', { href: '/login' }, 'Back to log in')]),
        ]),
      );
    } catch (err) {
      error.textContent =
        err instanceof ApiClientError ? err.message : 'Something went wrong. Try again.';
      error.hidden = false;
      submit.removeAttribute('disabled');
    }
  });

  outlet.append(
    el('main', { class: 'page' }, [
      authBrand(),
      el('h1', {}, 'Contact us'),
      el('p', {}, "Have a question or need help? Send us a message and we'll get back to you."),
      form,
      el('p', { class: 'signed-out-only' }, [el('a', { href: '/login' }, 'Back to log in')]),
    ]),
  );
};
